import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Stamp } from "./booklet";
import StampForm from "@/components/stamp-form"; 
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Search,
  Navigation,
  Globe,
  ArrowRight,
  Landmark,
  Building2,
  TowerControl,
  Palmtree,
  Mountain,
  Waves,
  Castle,
  Hash,
  MapPin,
  CircleDot,
} from "lucide-react";

// --- MAPPING UTILITIES ---
const COUNTRY_TO_ISO: Record<string, string> = {
  "Afghanistan": "af", "Albania": "al", "Algeria": "dz", "Andorra": "ad", "Angola": "ao", "Antigua and Barbuda": "ag", "Argentina": "ar", "Armenia": "am", "Australia": "au", "Austria": "at", "Azerbaijan": "az",
  "Bahamas": "bs", "Bahrain": "bh", "Bangladesh": "bd", "Barbados": "bb", "Belarus": "by", "Belgium": "be", "Belize": "bz", "Benin": "bj", "Bhutan": "bt", "Bolivia": "bo", "Bosnia and Herzegovina": "ba", "Botswana": "bw", "Brazil": "br", "Brunei": "bn", "Bulgaria": "bg", "Burkina Faso": "bf", "Burundi": "bi",
  "Cabo Verde": "cv", "Cambodia": "kh", "Cameroon": "cm", "Canada": "ca", "Central African Republic": "cf", "Chad": "td", "Chile": "cl", "China": "cn", "Colombia": "co", "Comoros": "km", "Congo": "cg", "Costa Rica": "cr", "Croatia": "hr", "Cuba": "cu", "Cyprus": "cy", "Czech Republic": "cz",
  "Denmark": "dk", "Djibouti": "dj", "Dominica": "dm", "Dominican Republic": "do", "Ecuador": "ec", "Egypt": "eg", "El Salvador": "sv", "Equatorial Guinea": "gq", "Eritrea": "er", "Estonia": "ee", "Eswatini": "sz", "Ethiopia": "et",
  "Fiji": "fj", "Finland": "fi", "France": "fr", "Gabon": "ga", "Gambia": "gm", "Georgia": "ge", "Germany": "de", "Ghana": "gh", "Greece": "gr", "Grenada": "gd", "Guatemala": "gt", "Guinea": "gn", "Guinea-Bissau": "gw", "Guyana": "gy",
  "Haiti": "ht", "Honduras": "hn", "Hungary": "hu", "Iceland": "is", "India": "in", "Indonesia": "id", "Iran": "ir", "Iraq": "iq", "Ireland": "ie", "Israel": "il", "Italy": "it", "Jamaica": "jm", "Japan": "jp", "Jordan": "jo",
  "Kazakhstan": "kz", "Kenya": "ke", "Kiribati": "ki", "Korea, North": "kp", "Korea, South": "kr", "Kosovo": "xk", "Kuwait": "kw", "Kyrgyzstan": "kg", "Laos": "la", "Latvia": "lv", "Lebanon": "lb", "Lesotho": "ls", "Liberia": "lr", "Libya": "ly", "Liechtenstein": "li", "Lithuania": "lt", "Luxembourg": "lu",
  "Madagascar": "mg", "Malawi": "mw", "Malaysia": "my", "Maldives": "mv", "Mali": "ml", "Malta": "mt", "Marshall Islands": "mh", "Mauritania": "mr", "Mauritius": "mu", "Mexico": "mx", "Micronesia": "fm", "Moldova": "md", "Monaco": "mc", "Mongolia": "mn", "Montenegro": "me", "Morocco": "ma", "Mozambique": "mz", "Myanmar": "mm",
  "Namibia": "na", "Nauru": "nr", "Nepal": "np", "Netherlands": "nl", "New Zealand": "nz", "Nicaragua": "ni", "Niger": "ne", "Nigeria": "ng", "North Macedonia": "mk", "Norway": "no", "Oman": "om",
  "Pakistan": "pk", "Palau": "pw", "Palestine": "ps", "Panama": "pa", "Papua New Guinea": "pg", "Paraguay": "py", "Peru": "pe", "Philippines": "ph", "Poland": "pl", "Portugal": "pt", "Qatar": "qa",
  "Romania": "ro", "Russia": "ru", "Rwanda": "rw", "Saint Kitts and Nevis": "kn", "Saint Lucia": "lc", "Saint Vincent": "vc", "Samoa": "ws", "San Marino": "sm", "Sao Tome and Principe": "st", "Saudi Arabia": "sa", "Senegal": "sn", "Serbia": "rs", "Seychelles": "sc", "Sierra Leone": "sl", "Singapore": "sg", "Slovakia": "sk", "Slovenia": "si", "Solomon Islands": "sb", "Somalia": "so", "South Africa": "za", "South Sudan": "ss", "Spain": "es", "Sri Lanka": "lk", "Sudan": "sd", "Suriname": "sr", "Sweden": "se", "Switzerland": "ch", "Syria": "sy",
  "Taiwan": "tw", "Tajikistan": "tj", "Tanzania": "tz", "Thailand": "th", "Timor-Leste": "tl", "Togo": "tg", "Tonga": "to", "Trinidad and Tobago": "tt", "Tunisia": "tn", "Turkey": "tr", "Turkmenistan": "tm", "Tuvalu": "tv", "Uganda": "ug", "Ukraine": "ua", "United Arab Emirates": "ae", "United Kingdom": "gb", "United States": "us", "Uruguay": "uy", "Uzbekistan": "uz",
  "Vanuatu": "vu", "Vatican City": "va", "Venezuela": "ve", "Vietnam": "vn", "Yemen": "ye", "Zambia": "zm", "Zimbabwe": "zw"
};

const CITY_LOGOS: Record<string, any> = {
  "Paris": Landmark, "London": Building2, "New York": Building2, "Tokyo": TowerControl,
  "Florence": Castle, "Rome": Landmark, "Los Angeles": Palmtree, "San Francisco": Waves,
  "Denver": Mountain, "Barcelona": Landmark,
};

// --- STAMP COMPONENT ---
function TravelStamp({ location, date, type, port }: { location: string, date?: string, type: string, port?: string }) {
  const rotation = useMemo(() => (Math.random() * 8 - 4).toFixed(1), []);
  const countryCode = COUNTRY_TO_ISO[location] || "";

  return (
    <div className="flex items-center justify-center p-4 transition-transform hover:scale-105 active:scale-95 group">
      <div 
        style={{ transform: `rotate(${rotation}deg)` }}
        className={cn(
          "relative flex flex-col items-center justify-center bg-white shadow-sm border-black/10",
          type === 'country' 
            ? "w-40 h-40 rounded-full border-4 border-double" 
            : "w-44 h-32 rounded-none border-2 border-dashed"
        )}
      >
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" />
        
        <div 
          className="flex flex-col items-center gap-1 text-center"
          style={{ transform: `rotate(${-rotation}deg)` }}
        >
          {type === 'country' && countryCode && (
            <div className="w-7 h-5 bg-zinc-100 overflow-hidden border border-black/5 mb-1 grayscale-[0.2] opacity-80 shadow-sm">
               <img 
                 src={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`} 
                 alt=""
                 className="w-full h-full object-cover"
               />
            </div>
          )}
          
          <span className="font-serif text-sm font-bold leading-none px-2 uppercase tracking-tighter text-black">
            {location}
          </span>

          {port && (
            <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-black/60 mt-0.5 uppercase">
              {port}
            </span>
          )}
          
          <div className="w-8 border-t border-black/10 my-0.5" />
          
          <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-black/40">
            {date ? new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase() : "PNDG"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [searchQuery, setSearchQuery] = useState("");
  const [editingStamp, setEditingStamp] = useState<Stamp | null>(null);

  const { data: stamps = [], isLoading } = useQuery<Stamp[]>({
    queryKey: ["stamps"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stamps")
        .select("*")
        .order("stamp_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const stats = useMemo(() => {
    const cityStamps = stamps.filter(s => s.type === "city");
    const countryStamps = stamps.filter(s => s.type === "country");
    return {
      uniqueCities: Array.from(new Set(cityStamps.map(s => s.location))),
      uniqueCountries: Array.from(new Set(countryStamps.map(s => s.location))),
      totalCount: stamps.length,
      allCountries: countryStamps,
      allCities: cityStamps
    };
  }, [stamps]);

  const filteredStamps = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return stamps;
    
    return stamps.filter(s => 
      s.location.toLowerCase().includes(q) ||
      (s.port_of_entry && s.port_of_entry.toLowerCase().includes(q)) ||
      (s.field_notes && s.field_notes.toLowerCase().includes(q))
    );
  }, [stamps, searchQuery]);

  if (isLoading) return <div className="p-12"><Skeleton className="h-12 w-48 mb-8" /><Skeleton className="h-[400px] w-full" /></div>;

  return (
    <div className="p-8 space-y-10 max-w-7xl mx-auto animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-black/10 pb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-mono bg-black text-white px-2 py-0.5 tracking-tighter uppercase">
              Entries: {stats.totalCount.toString().padStart(3, '0')}
            </span>
          </div>
          <h2 className="font-serif text-3xl tracking-tighter text-black uppercase">Netport Dashboard</h2>
        </div>

        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20" />
          <input 
            placeholder="Search registry..." 
            className="w-full bg-transparent pl-10 h-10 border-b border-black/10 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-black transition-colors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </header>

      <Tabs defaultValue="overview" className="space-y-8">
        <TabsList className="bg-transparent border-b border-black/5 w-full justify-start rounded-none h-auto p-0 gap-8">
          {["overview", "all", "countries", "cities"].map((tab) => (
            <TabsTrigger 
              key={tab}
              value={tab} 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-0 py-4 font-mono text-[10px] uppercase tracking-[0.2em] transition-all"
            >
              {tab}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="outline-none space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="rounded-none border-black/10 shadow-none bg-zinc-50/50">
              <CardHeader className="pb-2"><CardTitle className="text-[10px] font-mono uppercase tracking-widest text-black/40 flex items-center gap-2"><Hash className="w-3 h-3" />Total Stamps</CardTitle></CardHeader>
              <CardContent><p className="text-4xl font-light">{stats.totalCount}</p></CardContent>
            </Card>
            <Card className="rounded-none border-black/10 shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-[10px] font-mono uppercase tracking-widest text-black/40 flex items-center gap-2"><Globe className="w-3 h-3" />Countries</CardTitle></CardHeader>
              <CardContent><p className="text-4xl font-light">{stats.uniqueCountries.length}</p></CardContent>
            </Card>
            <Card className="rounded-none border-black/10 shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-[10px] font-mono uppercase tracking-widest text-black/40 flex items-center gap-2"><MapPin className="w-3 h-3" />Cities</CardTitle></CardHeader>
              <CardContent><p className="text-4xl font-light">{stats.uniqueCities.length}</p></CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="all" className="outline-none">
          <div className="border border-black/10 rounded-none overflow-hidden">
            <div className="grid grid-cols-10 bg-zinc-50 border-b border-black/10 p-4 font-mono text-[9px] uppercase tracking-widest text-black/40">
              <div className="col-span-4">Location</div>
              <div className="col-span-2 text-center">Port</div>
              <div className="col-span-2 text-center">Type</div>
              <div className="col-span-2 text-right">Timestamp</div>
            </div>
            <div className="divide-y divide-black/[0.05] bg-white">
              {filteredStamps.length > 0 ? filteredStamps.map((stamp) => (
                <div 
                  key={stamp.id} 
                  onClick={() => setEditingStamp(stamp)} 
                  className="grid grid-cols-10 p-5 items-center hover:bg-zinc-50 transition-all cursor-pointer group"
                >
                  <div className="col-span-4 flex items-center gap-4">
                    <CircleDot className="w-2 h-2 text-black/10 group-hover:text-black transition-colors" />
                    <span className="font-serif text-xl tracking-tight text-black">{stamp.location}</span>
                  </div>
                  <div className="col-span-2 text-center font-mono text-xs font-bold text-black/60 uppercase">
                    {stamp.port_of_entry || "—"}
                  </div>
                  <div className="col-span-2 text-center font-mono text-[10px] uppercase opacity-40">{stamp.type}</div>
                  <div className="col-span-2 text-right font-mono text-[10px]">
                    {stamp.stamp_date ? new Date(stamp.stamp_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).toUpperCase() : "—"}
                  </div>
                </div>
              )) : (
                <div className="p-20 text-center font-mono text-[10px] uppercase tracking-widest text-black/20">
                  No records found in registry
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="countries" className="outline-none">
          <div className="flex flex-wrap justify-center gap-10 py-8">
            {stats.allCountries
              .filter(s => s.location.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((stamp) => (
                <div key={stamp.id} onClick={() => setEditingStamp(stamp)} className="cursor-pointer">
                  <TravelStamp 
                    location={stamp.location} 
                    date={stamp.stamp_date} 
                    type="country" 
                    port={stamp.port_of_entry}
                  />
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="cities" className="outline-none">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {stats.uniqueCities
              .filter(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((city) => {
                const CityIcon = CITY_LOGOS[city] || Navigation;
                return (
                  <div key={city} className="flex items-center justify-between py-6 border-b border-black/[0.08] group hover:bg-zinc-50 px-2 transition-all cursor-pointer">
                    <div className="flex items-center gap-6">
                      <div className="w-12 h-12 flex items-center justify-center border border-black/10 bg-white group-hover:border-black">
                        <CityIcon className="w-5 h-5 text-black/30 group-hover:text-black" />
                      </div>
                      <span className="font-serif text-xl leading-none">{city}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-black/10 group-hover:text-black group-hover:translate-x-1 transition-all" />
                  </div>
                );
              })}
          </div>
        </TabsContent>
      </Tabs>

      {editingStamp && (
        <StampForm open={!!editingStamp} onClose={() => setEditingStamp(null)} editStamp={editingStamp} />
      )}
    </div>
  );
}